import {useMemo} from 'react';
import type {
  WorkflowDefinitionResponse} from 'src/api/ProactApiClient';
import {
  IntervalTypeEnum,
  WorkflowDefinitionTriggerTypeEnum,
} from 'src/api/ProactApiClient';

interface WorkflowValidationResult {
  errors: Record<string, string>;
  sectionErrors: Record<string, string[]>;
  isValid: boolean;
}

export const useWorkflowValidation = (
  data: WorkflowDefinitionResponse
): WorkflowValidationResult => {
  return useMemo(() => {
    const errors: Record<string, string> = {};
    const sectionErrors: Record<string, string[]> = {};

    if (!data.name?.trim()) {
      errors['name'] = 'Workflow name is required.';
    }

    // Trigger validation
    const triggerErrors: string[] = [];
    if (data.triggers.length === 0) {
      triggerErrors.push('At least one trigger is required.');
    }
    data.triggers.forEach((trigger, i) => {
      if (trigger.type === WorkflowDefinitionTriggerTypeEnum.Schedule) {
        if (!trigger.scheduleStartDate) {
          const key = `trigger.${i}.scheduleStartDate`;
          errors[key] = 'Start date is required.';
          triggerErrors.push(`Trigger ${i + 1}: Start date is required.`);
        }
        if (!trigger.scheduleEndDate) {
          const key = `trigger.${i}.scheduleEndDate`;
          errors[key] = 'End date is required.';
          triggerErrors.push(`Trigger ${i + 1}: End date is required.`);
        }
        if (trigger.scheduleHour == null) {
          const key = `trigger.${i}.scheduleHour`;
          errors[key] = 'Hour is required.';
          triggerErrors.push(`Trigger ${i + 1}: Hour is required.`);
        }
        if (
          trigger.scheduleIntervalType === IntervalTypeEnum.Frequency &&
          !trigger.scheduleIntervalDays
        ) {
          const key = `trigger.${i}.scheduleIntervalDays`;
          errors[key] = 'Frequency interval is required.';
          triggerErrors.push(
            `Trigger ${i + 1}: Frequency interval is required.`
          );
        }
      }
      if (trigger.type === WorkflowDefinitionTriggerTypeEnum.FormSubmission) {
        if (!trigger.triggerFormDefinitionId) {
          const key = `trigger.${i}.formDefinitionId`;
          errors[key] = 'Form definition is required.';
          triggerErrors.push(
            `Trigger ${i + 1}: Form definition is required.`
          );
        }
      }
    });
    if (triggerErrors.length > 0) {
      sectionErrors['triggers'] = triggerErrors;
    }

    // Phase validation
    const phaseErrors: string[] = [];
    if (data.phases.length === 0) {
      phaseErrors.push('At least one phase is required.');
    }
    const slugPattern = /^[A-Z][A-Z0-9_]{0,19}$/;
    const seenSlugs = new Set<string>();
    data.phases.forEach((phase, i) => {
      if (!phase.name?.trim()) {
        const key = `phase.${i}.name`;
        errors[key] = 'Phase name is required.';
        phaseErrors.push(`Phase ${i + 1}: Name is required.`);
      }
      if (phase.slug) {
        if (!slugPattern.test(phase.slug)) {
          const key = `phase.${i}.slug`;
          errors[key] =
            'Slug must start with a letter, contain only uppercase letters, digits, and underscores (max 20 chars).';
          phaseErrors.push(`Phase ${i + 1}: Invalid slug format.`);
        } else if (seenSlugs.has(phase.slug)) {
          const key = `phase.${i}.slug`;
          errors[key] = 'Duplicate slug. Each phase slug must be unique.';
          phaseErrors.push(`Phase ${i + 1}: Duplicate slug '${phase.slug}'.`);
        } else {
          seenSlugs.add(phase.slug);
        }
      }
      if (phase.actions.length === 0) {
        phaseErrors.push(
          `Phase ${i + 1}: At least one action is required.`
        );
      }
    });
    if (phaseErrors.length > 0) {
      sectionErrors['phases'] = phaseErrors;
    }

    const allErrors = [
      ...Object.values(errors),
      ...Object.values(sectionErrors).flat(),
    ];

    return {
      errors,
      sectionErrors,
      isValid: allErrors.length === 0,
    };
  }, [data]);
};
